import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getErrorMessage } from '@/shared/api/api-error';

import {
  adjustProviderBalance,
  decideBalanceApproval,
  getBalanceApprovals,
  createMapping,
  createMappings,
  createProvider,
  deleteProvider,
  getMappings,
  getProvider,
  getProviders,
  setMappingStatus,
  setProviderStatus,
  updateMapping,
  updateProviderBalanceSettings,
  getProviderAvailability,
} from './providers.api';
import type {
  CreateMappingPayload,
  CreateMappingsPayload,
  CreateProviderPayload,
  MappingListParams,
  MappingPayload,
  ProviderListParams,
  ProviderAvailabilityParams,
} from '../types/provider.types';

/**
 * Query keys. Provider and mapping changes also invalidate the brands
 * feature: they change which provider each item uses and whether it sells.
 */
export const providerKeys = {
  all: ['providers'] as const,
  lists: () => [...providerKeys.all, 'list'] as const,
  list: (params: ProviderListParams) => [...providerKeys.lists(), params] as const,
  details: () => [...providerKeys.all, 'detail'] as const,
  detail: (providerId: number) => [...providerKeys.details(), providerId] as const,
  options: () => [...providerKeys.all, 'options'] as const,
  balanceApprovals: (providerId: number) =>
    [...providerKeys.all, 'balance-approvals', providerId] as const,
  lowBalanceCount: () => [...providerKeys.all, 'low-balance-count'] as const,
  availability: (providerId: number, params: ProviderAvailabilityParams) =>
    [...providerKeys.all, 'availability', providerId, params] as const,
};

export const mappingKeys = {
  all: ['mappings'] as const,
  list: (params: MappingListParams) => [...mappingKeys.all, 'list', params] as const,
};

/** The brands feature's root key — kept literal to avoid importing that feature. */
const BRANDS_KEY = ['brands'] as const;

function useInvalidateSupply() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: providerKeys.all });
    void queryClient.invalidateQueries({ queryKey: mappingKeys.all });
    void queryClient.invalidateQueries({ queryKey: BRANDS_KEY });
  };
}

export function useProviders(params: ProviderListParams) {
  return useQuery({
    queryKey: providerKeys.list(params),
    queryFn: ({ signal }) => getProviders(params, signal),
    placeholderData: (previous) => previous,
  });
}

/** Every provider, for pickers. */
export function useProviderOptions() {
  return useQuery({
    queryKey: providerKeys.options(),
    queryFn: async ({ signal }) => (await getProviders({ page: 1, pageSize: 100 }, signal)).items,
    staleTime: 5 * 60_000,
  });
}

/**
 * How many providers are at or below their low-balance alarm, for the sidebar
 * badge. Asks for one row and reads the total. Refreshes every minute and after
 * any provider change (the key sits under `providerKeys.all`). Pass false
 * without providers.read, so nothing is requested.
 */
export function useLowBalanceProviderCount(enabled: boolean) {
  return useQuery({
    queryKey: providerKeys.lowBalanceCount(),
    queryFn: async ({ signal }) =>
      (await getProviders({ page: 1, pageSize: 1, lowBalance: true }, signal)).totalItems,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useProvider(providerId: number | undefined) {
  return useQuery({
    queryKey: providerKeys.detail(providerId ?? 0),
    queryFn: ({ signal }) => getProvider(providerId as number, signal),
    enabled: providerId !== undefined,
  });
}

export function useProviderAvailability(providerId: number, params: ProviderAvailabilityParams) {
  return useQuery({
    queryKey: providerKeys.availability(providerId, params),
    queryFn: ({ signal }) => getProviderAvailability(providerId, params, signal),
    placeholderData: (previous) => previous,
  });
}

export function useMappings(params: MappingListParams) {
  return useQuery({
    queryKey: mappingKeys.list(params),
    queryFn: ({ signal }) => getMappings(params, signal),
    placeholderData: (previous) => previous,
  });
}

/** Field errors (a name in use, an unknown currency) are shown in the form by the caller. */
export function useCreateProvider() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (payload: CreateProviderPayload) => createProvider(payload),
    onSuccess: (_id, { name }) => {
      toast.success(`Provider "${name}" created.`);
      invalidate();
    },
  });
}

export function useSetProviderStatus() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (input: { providerId: number; isActive: boolean; reason: string | null }) =>
      setProviderStatus(input.providerId, input.isActive, input.reason),
    onSuccess: (changed, { isActive }) => {
      toast.success(
        changed === 0
          ? 'Nothing to change.'
          : isActive
            ? 'Provider activated.'
            : 'Provider deactivated.',
      );
      invalidate();
    },
    // Field errors (a missing reason) are shown in the dialog by the caller.
  });
}

/** Deletes an unused provider. The API's refusal is shown as a toast. */
export function useDeleteProvider() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (input: { providerId: number; name: string; reason: string | null }) =>
      deleteProvider(input.providerId, input.reason),
    onSuccess: (_result, { providerId, name }) => {
      toast.success(`Provider "${name}" deleted.`);
      queryClient.removeQueries({ queryKey: providerKeys.detail(providerId) });
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not delete the provider.'));
      // A 409 means something started using it meanwhile — refresh the counts.
      invalidate();
    },
  });
}

export function useUpdateProviderBalanceSettings() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (input: {
      providerId: number;
      isBalanceConsidered: boolean;
      lowBalanceThreshold: number | null;
    }) =>
      updateProviderBalanceSettings(input.providerId, {
        isBalanceConsidered: input.isBalanceConsidered,
        lowBalanceThreshold: input.lowBalanceThreshold,
      }),
    onSuccess: () => {
      toast.success('Balance settings saved.');
      invalidate();
    },
  });
}

export function useAdjustProviderBalance() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (input: { providerId: number; amount: number; reason: string }) =>
      adjustProviderBalance(input.providerId, input.amount, input.reason),
    onSuccess: (result) => {
      toast.success(
        result.applied
          ? 'Balance adjusted.'
          : 'Above the approval threshold — sent for approval. The balance changes once it is approved.',
      );
      invalidate();
    },
  });
}

export function useCreateMapping() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (payload: CreateMappingPayload) => createMapping(payload),
    onSuccess: () => {
      toast.success('Provider mapped.');
      invalidate();
    },
  });
}

export function useCreateMappings() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (payload: CreateMappingsPayload) => createMappings(payload),
    onSuccess: (ids) => {
      toast.success(ids.length === 1 ? 'Provider mapped.' : `${ids.length} providers mapped.`);
      invalidate();
    },
  });
}

export function useUpdateMapping() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: ({ mappingId, payload }: { mappingId: number; payload: MappingPayload }) =>
      updateMapping(mappingId, payload),
    onSuccess: () => {
      toast.success('Mapping updated.');
      invalidate();
    },
  });
}

export function useSetMappingStatus() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (input: { mappingId: number; isActive: boolean; reason: string | null }) =>
      setMappingStatus(input.mappingId, input.isActive, input.reason),
    onSuccess: (changed, { isActive }) => {
      toast.success(
        changed === 0
          ? 'Nothing to change.'
          : isActive
            ? 'Mapping activated.'
            : 'Mapping deactivated.',
      );
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not change the mapping.'));
    },
  });
}

/** Pending balance adjustment requests for one provider. */
export function useBalanceApprovals(providerId: number) {
  return useQuery({
    queryKey: providerKeys.balanceApprovals(providerId),
    queryFn: ({ signal }) => getBalanceApprovals(providerId, 'Pending', signal),
  });
}

export function useDecideBalanceApproval() {
  const invalidate = useInvalidateSupply();
  return useMutation({
    mutationFn: (input: {
      approvalId: number;
      decision: 'approve' | 'reject' | 'cancel';
      reason: string | null;
    }) => decideBalanceApproval(input.approvalId, input.decision, input.reason),
    onSuccess: (_result, input) => {
      toast.success(
        input.decision === 'approve'
          ? 'Adjustment approved and applied.'
          : input.decision === 'reject'
            ? 'Adjustment rejected.'
            : 'Request withdrawn.',
      );
      invalidate();
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Could not decide the request.')),
  });
}
